/**
 * Echo Hunt -- the deterministic text layer (P4, §4.3 + §7).
 *
 * Two responsibilities, both PURE and fully unit-tested:
 *
 *  1. `NormalizeHouseStyle(text)` -- the deterministic subset of §4.3: flatten the user's own
 *     idiosyncratic punctuation habits (`--` dashes, `word1/word2`, `()`/`[]` parentheticals,
 *     recall hedges) so that real and generated bullets share one surface style.
 *     The LLM half of §4.3 (rewriting bullets into the house style) is P5's index pass; this module
 *     only does what a regex can do safely and reproducibly.
 *
 *  2. `CheckBullet(text, opts)` -- the §7 final gate. Returns every violation it finds; each one
 *     triggers regeneration of that bullet. It covers:
 *      - the user's known patterns (§4.3);
 *      - banned terms, INFLECTION-AWARE (`lantern` also matches `lanterns`, `lantern-lit`,
 *        `lantern's`);
 *      - cliché-blacklist phrases (§5.5).
 *
 * Splitting the two matters: normalization fixes what we can fix mechanically, and the check reports
 * what we cannot (so the caller regenerates rather than shipping a bullet with a tell in it).
 *
 * PURE module: no React, no `store`, no DB imports (see README.md).
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §4.3, §7, §16.2 P4.
 */

// house style (§4.3)
// ==========

/** Recall hedges that mark a bullet as reconstructed rather than experienced (§4.3). These are
 * REMOVED by `NormalizeHouseStyle` and their presence (before normalization) is reported by
 * `CheckBullet` as `hedge`, which is what feeds the `hedgeDominated` filter in P5. */
export const RecallHedge_values = [
	"i think it was",
	"i think there was",
	"i think i",
	"i think that",
	"i think",
	"i guess",
	"i believe",
	"not sure about this part",
	"not sure about",
	"not sure if",
	"not sure",
	"hard to say",
	"hard to remember",
	"hard to put into words",
	"can't recall",
	"cant recall",
	"can't remember",
	"dont remember",
	"don't remember",
	"partly recall",
	"bit difficult",
	"somewhat vague",
	"vaguely recall",
	"if i recall",
	"i vaguely",
] as const;

/** Replaces `--`/`---` dashes and the `word1/word2` slash form, and unwraps `()`/`[]` parentheticals,
 * leaving their content inline. Deliberately conservative: anything ambiguous is left alone for the
 * §7 check to flag rather than silently mangled. */
export function NormalizeHouseStyle(text: string): string {
	let result = text;

	// `()` / `[]` parentheticals -> inline content.
	// Only unwrap when the bracketed run has no nested brackets and is on one line, so we never eat
	// half a sentence on an unbalanced paren.
	result = result.replace(/\(([^()\n]*)\)/g, (_, inner: string)=>inner.trim());
	result = result.replace(/\[([^[\]\n]*)\]/g, (_, inner: string)=>inner.trim());

	// `word1/word2` -> `word1 or word2` (the user's shorthand for "either of these")
	result = result.replace(/(\w)\/(\w)/g, "$1 or $2");

	// `--` / `---` dashes -> a comma, since the house style has no dash habit
	result = result.replace(/\s*--+\s*/g, ", ");

	// recall hedges, longest first so "i think it was" wins over "i think"
	const hedges = [...RecallHedge_values].sort((a, b)=>b.length - a.length);
	for (const hedge of hedges) {
		// word-boundary-ish: the hedge may be followed by punctuation, so anchor on a non-letter
		const re = new RegExp(`(^|[^a-z])${EscapeRegExp(hedge)}(?=[^a-z]|$)`, "gi");
		result = result.replace(re, (_match, prefix: string)=>prefix);
	}

	// collapse leftovers: doubled spaces/commas, space before punctuation, leading/trailing junk
	result = result.replace(/\s*,\s*,+/g, ",");
	result = result.replace(/\s+([,.;:!?])/g, "$1");
	result = result.replace(/,\s*([.;:!?])/g, "$1");
	result = result.replace(/[ \t]{2,}/g, " ");
	result = result.replace(/^[\s,;:.]+/, "");
	result = result.replace(/[\s,;:]+$/, "");

	return result.trim();
}

// the §7 check
// ==========

export const BulletViolationKind_values = [
	/** a banned term appears (§4.4, §7) */
	"bannedTerm",
	/** a cliché-blacklist phrase appears (§5.5, §7) */
	"cliche",
	/** one of the user's own punctuation habits survived normalization (§4.3, §7) */
	"houseStylePattern",
	/** a recall hedge is present (§4.3, §4.4 `hedgeDominated`) */
	"hedge",
	/** shorter than the §4.4 length band */
	"tooShort",
	/** an empty/whitespace-only bullet */
	"empty",
] as const;
export type BulletViolationKind = typeof BulletViolationKind_values[number];

export type BulletViolation = {
	kind: BulletViolationKind;
	/** the offending substring, as it appeared in the text */
	match: string;
	/** a short human-readable explanation (shown in dev tools and the Corpus tab) */
	message: string;
};

export type CheckBulletOptions = {
	/** terms that must never appear, matched inflection-aware (§4.4 `bannedTerm`, §7) */
	bannedWords?: string[]|n;
	/** phrases that must never appear (cliché blacklist, §5.5) */
	blacklist?: string[]|n;
	/** minimum word count; below this the bullet is `tooShort` (§4.4 granularity). Default 6. */
	minWords?: number|n;
};

/** Minimum words in a usable bullet -- the §4.4 "granularity: length band" default. */
export const DEFAULT_MIN_BULLET_WORDS = 6;

/** Runs the deterministic §7 checks over one bullet. Returns an empty array when the bullet is clean.
 *
 * `bannedWords` matching is INFLECTION-AWARE: the word matches as a whole word plus any of the
 * common suffixes/joins that keep it recognisable (`-s`, `-es`, `-ed`, `-ing`, `-lit`, `'s`, and
 * hyphen compounds such as `lantern-lit`). That's the §7 requirement ("including base forms and
 * inflections"), and it's why a plain substring search would be wrong in both directions. */
export function CheckBullet(text: string, opts: CheckBulletOptions = {}): BulletViolation[] {
	const violations: BulletViolation[] = [];
	const trimmed = (text ?? "").trim();

	if (trimmed.length == 0) {
		violations.push({kind: "empty", match: "", message: "The bullet is empty."});
		return violations;
	}

	const minWords = opts.minWords ?? DEFAULT_MIN_BULLET_WORDS;
	const wordCount = CountWords(trimmed);
	if (wordCount < minWords) {
		violations.push({kind: "tooShort", match: trimmed, message: `The bullet has ${wordCount} words; the minimum is ${minWords}.`});
	}

	// banned terms (inflection-aware)
	for (const rawWord of opts.bannedWords ?? []) {
		const word = rawWord.trim();
		if (word.length == 0) continue;
		const re = BannedWordMatcher(word);
		for (const match of trimmed.matchAll(re)) {
			violations.push({
				kind: "bannedTerm", match: match[0],
				message: `Banned term "${word}" appears (as "${match[0]}").`,
			});
			break; // one report per term is enough
		}
	}

	// cliché blacklist (phrase, not inflection-aware -- these are deliberate phrasings)
	for (const rawPhrase of opts.blacklist ?? []) {
		const phrase = rawPhrase.trim();
		if (phrase.length == 0) continue;
		const re = new RegExp(EscapeRegExp(phrase).replace(/\s+/g, "\\s+"), "i");
		const match = re.exec(trimmed);
		if (match != null) {
			violations.push({
				kind: "cliche", match: match[0],
				message: `Blacklisted cliché "${phrase}" appears.`,
			});
		}
	}

	// the user's own punctuation habits (§4.3) -- these should NOT survive `NormalizeHouseStyle`
	if (/--/.test(trimmed)) {
		violations.push({kind: "houseStylePattern", match: "--", message: "Contains a `--` dash (§4.3)."});
	}
	const slashMatch = /\w\/\w/.exec(trimmed);
	if (slashMatch != null) {
		violations.push({kind: "houseStylePattern", match: slashMatch[0], message: "Contains a `word/word` slash form (§4.3)."});
	}
	const parenMatch = /[([][^)\]]*[)\]]/.exec(trimmed);
	if (parenMatch != null) {
		violations.push({kind: "houseStylePattern", match: parenMatch[0], message: "Contains a () or [] parenthetical (§4.3)."});
	}

	// recall hedges (§4.3)
	for (const hedge of RecallHedge_values) {
		const re = new RegExp(`(^|[^a-z])${EscapeRegExp(hedge)}(?=[^a-z]|$)`, "i");
		const match = re.exec(trimmed);
		if (match != null) {
			violations.push({kind: "hedge", match: hedge, message: `Contains the recall hedge "${hedge}" (§4.3).`});
			break;
		}
	}

	return violations;
}

/** True when `CheckBullet` found nothing. Convenience for callers that only branch on pass/fail. */
export function IsBulletClean(text: string, opts: CheckBulletOptions = {}): boolean {
	return CheckBullet(text, opts).length == 0;
}

/** True when the hedges alone make the bullet unusable (§4.4 `hedgeDominated`): the bullet is mostly
 * hedging, rather than an event with a hedge attached. Measured as hedge-word share of total words. */
export function IsHedgeDominated(text: string, threshold = .3): boolean {
	const words = CountWords(text);
	if (words == 0) return false;
	const lower = text.toLowerCase();
	let hedgeWords = 0;
	for (const hedge of RecallHedge_values) {
		const re = new RegExp(`(^|[^a-z])${EscapeRegExp(hedge)}(?=[^a-z]|$)`, "gi");
		for (const match of lower.matchAll(re)) {
			hedgeWords += CountWords(match[0]);
		}
	}
	return hedgeWords / words >= threshold;
}

// helpers
// ==========

export function CountWords(text: string): number {
	const matches = text.trim().match(/[A-Za-z0-9']+/g);
	return matches?.length ?? 0;
}

/** Builds the inflection-aware matcher for one banned word.
 *
 * `lantern` must match: `lantern`, `lanterns`, `Lantern`, `lantern-lit`, `lantern's`, `lanterned`.
 * It must NOT match inside an unrelated word that merely contains the letters (`lanternfly` is a real
 * different thing, but we accept the false positive: banning is the safe direction -- see §6, where a
 * leaked seed word is a hard tell).
 */
export function BannedWordMatcher(word: string): RegExp {
	const escaped = EscapeRegExp(word);
	// word-boundary at the start; at the end allow the common inflections and hyphen compounds
	return new RegExp(`(^|[^a-z0-9])${escaped}(s|es|ed|ing|lit|like|ish|'s)?(?=[^a-z0-9]|$)`, "gi");
}

export function EscapeRegExp(text: string): string {
	return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
